import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/* eslint-disable react-refresh/only-export-components */
import { MessageSquare, X } from 'lucide-react';
import { createContext, useCallback, useContext, useEffect, useRef, useState, } from 'react';
import { Button } from './Button';
import { cn } from '@/lib/utils';
const Ctx = createContext(null);
export function PromptProvider({ children }) {
    const [active, setActive] = useState(null);
    const prompt = useCallback((opts) => {
        return new Promise((resolve) => {
            setActive({ ...opts, resolve });
        });
    }, []);
    const finish = useCallback((value) => {
        if (active)
            active.resolve(value);
        setActive(null);
    }, [active]);
    useEffect(() => {
        if (!active)
            return;
        const onKey = (e) => {
            if (e.key === 'Escape')
                finish(null);
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [active, finish]);
    return (_jsxs(Ctx.Provider, { value: { prompt }, children: [children, active && _jsx(PromptModal, { modal: active, onFinish: finish })] }));
}
export function usePrompt() {
    const ctx = useContext(Ctx);
    if (ctx === null)
        throw new Error('usePrompt must be used within PromptProvider');
    return ctx;
}
function PromptModal({ modal, onFinish, }) {
    const [value, setValue] = useState(modal.initialValue ?? '');
    const inputRef = useRef(null);
    useEffect(() => {
        // The first focusable in the modal — autofocus the input.
        inputRef.current?.focus();
        inputRef.current?.select?.();
    }, []);
    const canConfirm = !modal.required || value.trim().length > 0;
    const submit = () => {
        if (!canConfirm)
            return;
        onFinish(value);
    };
    return (_jsxs("div", { role: "dialog", "aria-modal": "true", "aria-labelledby": "prompt-title", className: "fixed inset-0 z-[110] flex items-end justify-center p-4 sm:items-center", children: [_jsx("button", { type: "button", onClick: () => onFinish(null), "aria-label": "Dismiss", className: "fixed inset-0 bg-foreground/50 backdrop-blur-sm" }), _jsxs("div", { className: cn('relative z-[1] w-full max-w-md rounded-xl border bg-card p-5 shadow-xl', 'animate-in fade-in zoom-in-95 duration-150'), children: [_jsx("button", { type: "button", onClick: () => onFinish(null), className: "absolute right-3 top-3 rounded p-1 text-muted-foreground hover:bg-muted", "aria-label": "Close", children: _jsx(X, { className: "h-4 w-4" }) }), _jsxs("div", { className: "flex items-start gap-3", children: [_jsx("span", { className: "flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-blue-500/10 text-blue-600", children: _jsx(MessageSquare, { className: "h-5 w-5" }) }), _jsxs("div", { className: "min-w-0 flex-1", children: [_jsx("h2", { id: "prompt-title", className: "text-base font-semibold", children: modal.title }), modal.description && (_jsx("div", { className: "mt-1 text-sm text-muted-foreground", children: modal.description }))] })] }), _jsx("div", { className: "mt-4", children: modal.multiline ? (_jsx("textarea", { ref: inputRef, value: value, placeholder: modal.placeholder, onChange: (e) => setValue(e.target.value), onKeyDown: (e) => {
                                if (e.key === 'Enter' && (e.metaKey || e.ctrlKey))
                                    submit();
                            }, rows: 4, className: "block w-full rounded-md border border-input bg-background px-3 py-2 text-sm" })) : (_jsx("input", { ref: inputRef, type: "text", value: value, placeholder: modal.placeholder, onChange: (e) => setValue(e.target.value), onKeyDown: (e) => {
                                if (e.key === 'Enter')
                                    submit();
                            }, className: "block w-full rounded-md border border-input bg-background px-3 py-2 text-sm" })) }), _jsxs("div", { className: "mt-5 flex justify-end gap-2", children: [_jsx(Button, { variant: "ghost", onClick: () => onFinish(null), children: modal.cancelLabel ?? 'Cancel' }), _jsx(Button, { variant: "primary", disabled: !canConfirm, onClick: submit, children: modal.confirmLabel ?? 'OK' })] })] })] }));
}
